import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';
import { safeContinuePath } from '../utils/safeContinuePath';
import { landAfterSignIn } from '../utils/signInLanding';

/**
 * Central's change-password page: replaces an admin-issued temporary password,
 * and handles an ordinary (voluntary) change with the current password.
 */
export const CHANGE_PASSWORD_PATH = '/auth/change-password';

const MIN_LENGTH = 10;

/**
 * Which change this is. `temporary`: an admin issued a temporary password and
 * the session may not do anything else until it is replaced (no current
 * password needed — the session was opened with it). `voluntary`: an ordinary
 * change, which the server only accepts with the current password.
 * `unknown`: the status read failed — ask for the current password anyway
 * (the server ignores it for a temporary password, so this is never wrong).
 */
export type ChangePasswordMode = 'temporary' | 'voluntary' | 'unknown';

/** Machine code → what the person should be told (codes from /api/auth/session/change-password). */
export function changePasswordErrorMessage(code: string | undefined, fallback: string | undefined, retryAfter?: number): string {
  switch (code) {
    case 'CURRENT_PASSWORD_INCORRECT':
      return 'Your current password is incorrect.';
    case 'PASSWORD_UNCHANGED':
      return fallback || 'Choose a password that is different from your current one.';
    case 'WEAK_PASSWORD':
      return `Use at least ${MIN_LENGTH} characters.`;
    case 'TOO_MANY_PASSWORD_ATTEMPTS': {
      const minutes = retryAfter ? Math.max(1, Math.ceil(retryAfter / 60)) : null;
      return `Too many incorrect passwords.${minutes ? ` Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.` : ' Try again later.'}`;
    }
    default:
      return fallback || 'Could not change your password. Try again.';
  }
}

/**
 * Choose a new password, from the Central session (no token needed).
 *
 * Reached when a Central admin issued a temporary password: sign-in succeeds,
 * then this page comes before anything else. The server refuses every app,
 * token and session bridge until the change is made, so skipping this page
 * gets the user nowhere. On success the page resumes `continue` (a suite-app
 * authorize URL, or a Central page), or lands the user as sign-in would.
 */
export function ChangePasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const continueUrl = safeContinuePath(params.get('continue')) ?? '';
  const [mode, setMode] = useState<ChangePasswordMode>('unknown');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loginForThisPage = () => {
    const back = `${CHANGE_PASSWORD_PATH}${continueUrl ? `?continue=${encodeURIComponent(continueUrl)}` : ''}`;
    navigate(`/auth/login?continue=${encodeURIComponent(back)}`, { replace: true });
  };

  // Temporary or voluntary? GET /api/workspace/current (session cookie, read
  // only) answers status 'password-change-required' exactly when an admin-set
  // temporary password is pending; 401 means there is no session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/workspace/current', { credentials: 'include' });
        if (cancelled) return;
        if (res.status === 401) {
          loginForThisPage();
          return;
        }
        if (!res.ok) return;
        const body = (await res.json().catch(() => ({}))) as { status?: string };
        if (!cancelled) setMode(body.status === 'password-change-required' ? 'temporary' : 'voluntary');
      } catch {
        // stay 'unknown': the current-password field is shown and harmless
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = mode === 'voluntary' ? 'Change your password · LevelUp' : 'Choose a new password · LevelUp';
  }, [mode]);

  const asksCurrent = mode !== 'temporary';

  const goOn = async () => {
    if (continueUrl.startsWith('/api/')) {
      window.location.assign(continueUrl);
      return;
    }
    if (continueUrl) {
      navigate(continueUrl, { replace: true });
      return;
    }
    await landAfterSignIn(navigate, { replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (mode === 'voluntary' && !currentPassword) {
      setError('Enter your current password.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/session/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          asksCurrent && currentPassword ? { currentPassword, newPassword: password } : { newPassword: password },
        ),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string; retryAfter?: number };
      if (res.status === 401) {
        loginForThisPage();
        return;
      }
      if (!res.ok) {
        setError(changePasswordErrorMessage(body.code, body.error, body.retryAfter));
        if (body.code === 'CURRENT_PASSWORD_INCORRECT') setCurrentPassword('');
        return;
      }
      toast.success('Password changed');
      await goOn();
    } catch {
      setError('Could not reach Central. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const input =
    'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              {mode === 'voluntary' ? 'Change your password' : 'Choose a new password'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === 'voluntary'
                ? 'Enter your current password, then choose a new one. Your other signed-in devices will be signed out.'
                : mode === 'temporary'
                  ? 'Your administrator gave you a temporary password. Pick your own to continue.'
                  : 'Pick a new password to continue.'}
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4" noValidate>
            {asksCurrent && (
              <div>
                <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'voluntary' ? 'Current password' : 'Current password (if you have one)'}
                </label>
                <input
                  id="current-password"
                  type={show ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={input}
                />
              </div>
            )}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                New password
              </label>
              <input
                id="new-password"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus={!asksCurrent}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={input}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={input}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
              Show passwords
            </label>
            {error && (
              <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
            >
              {busy ? 'Saving…' : mode === 'voluntary' ? 'Change password' : 'Save and continue'}
            </button>
          </form>
          <p className="mt-6 text-center text-xs text-gray-500">At least {MIN_LENGTH} characters.</p>
          {asksCurrent && (
            <p className="mt-2 text-center text-xs text-gray-500" data-testid="no-password-hint">
              Signed in with Google and never set a password?{' '}
              <a href="/auth/forgot-password" className="font-medium text-green-700 hover:underline">
                Set one by email
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
